// Client-side helpers. Config (categories/subtotals/KPIs) is fetched from the
// server (getManualReportRoleConfig) at app boot and passed in as a param.

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const SHORT_MONTHS = MONTHS.map(m => m.slice(0, 3));

export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const SHORT_DAYS = DAYS.map(d => d.slice(0, 3));

export const weekOfMonth = (dayOfMonth) => Math.ceil(dayOfMonth / 7);

export const weeksInMonth = (year, monthIndex) => {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return weekOfMonth(lastDay);
};

export const dateForCell = (year, monthIndex, week, dayLabel) => {
  const dayIdx = DAYS.indexOf(dayLabel);
  if (dayIdx < 0) return null;
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const start = (week - 1) * 7 + 1;
  const end = Math.min(week * 7, lastDay);
  for (let d = start; d <= end; d++) {
    const dt = new Date(Date.UTC(year, monthIndex, d));
    const jsDow = dt.getUTCDay();
    const ourDow = jsDow === 0 ? 6 : jsDow - 1;
    if (ourDow === dayIdx) return dt;
  }
  return null;
};

export const formatDmy = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yy = String(d.getUTCFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
};

export const formatIso = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toISOString().slice(0, 10);
};

export const todayPeriod = () => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    monthIndex: now.getMonth(),
    week: weekOfMonth(now.getDate()),
    day: DAYS[(now.getDay() + 6) % 7],
  };
};

// ─── unified config helpers ─────────────────────────────────────────────
// Config shape (returned by server, parsed at app boot):
//   { categories: [{ key, label, fields: [{ key, label }] }],
//     subtotals:  [{ key, label, categoryKey }],
//     kpis:       [{ key, label, color, contributingFields: [] }] }

export const getCategories = (config) => config?.categories || [];

export const getAllFieldKeys = (config) =>
  getCategories(config).flatMap(c => c.fields.map(f => f.key));

export const computeSubtotals = (config, counts) => {
  const out = {};
  const cats = getCategories(config);
  for (const s of (config?.subtotals || [])) {
    const cat = cats.find(c => c.key === s.categoryKey);
    if (!cat) { out[s.key] = 0; continue; }
    out[s.key] = cat.fields.reduce((sum, f) => sum + (Number(counts?.[f.key]) || 0), 0);
  }
  return out;
};

export const computeDayGrandTotal = (config, counts) =>
  getAllFieldKeys(config).reduce((sum, k) => sum + (Number(counts?.[k]) || 0), 0);

export const sumCountsAcrossEntries = (entries) => {
  const totals = {};
  for (const e of entries) {
    const counts = e.countsJson ? JSON.parse(e.countsJson) : (e.counts || {});
    for (const [k, v] of Object.entries(counts)) {
      totals[k] = (totals[k] || 0) + (Number(v) || 0);
    }
  }
  return totals;
};

export const computeKPIs = (config, totals) => {
  if (!config?.kpis) return {};
  const out = {};
  for (const kpi of config.kpis) {
    out[kpi.key] = kpi.contributingFields.reduce((sum, f) => sum + (Number(totals?.[f]) || 0), 0);
  }
  out.total = Object.values(out).reduce((a, b) => a + b, 0);
  return out;
};

export const parseCounts = (entry) => {
  if (!entry) return {};
  if (entry.counts) return entry.counts;
  if (!entry.countsJson) return {};
  try { return JSON.parse(entry.countsJson) || {}; } catch { return {}; }
};
